import { FC, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { State } from 'index'
import styles from 'styles/components/card.module.scss'
import typography from 'styles/components/typography.module.scss'
import { StockType } from 'types/stock'
import { CardInner, ScheduledItemStockCard } from 'components/Card'
import { FlexRow } from 'components/Flex'
import { Spacer } from 'components/Utility'
import { SmallLabel } from 'components/Label'
import { GreyTag, TimeSaleTag } from 'components/Tag'
import { ScheduleType } from 'types/schedule'
import { BreadType } from 'types/bread'
import { CheckoutItemType } from 'types/checkoutItem'
import { getBreadShortName } from 'lib/helper'
import { StoreTimesale } from 'types/storeTimesale'
import { getStoreTimesale } from 'reducks/storeTimesale/selectors'
import { DayOfWeekType } from 'lib/daysOfWeek'
import { addMinutes, isBefore } from 'date-fns'

type Props = {
  bread: BreadType
  categoryName: string
  checkedOrderTypes: string[]
  currentTab: 'released' | 'unreleased'
  date: Date
  dayOfWeek: DayOfWeekType
  isAfterLastOrder: boolean
  isFuture: boolean
  isTemporaryClose: boolean
  dailyStocks: StockType[]
  preorderStocks: StockType[]
  dailySchedule: ScheduleType | undefined
  preorderSchedule: ScheduleType | undefined
  dailyCheckoutItems: CheckoutItemType[]
  preorderCheckoutItems: CheckoutItemType[]
}

const ScheduledItemCard: FC<Props> = (props) => {
  const {
    bread,
    categoryName,
    checkedOrderTypes,
    currentTab,
    date,
    dayOfWeek,
    isAfterLastOrder,
    isFuture,
    isTemporaryClose,
    dailyStocks,
    preorderStocks,
    dailySchedule,
    preorderSchedule,
    dailyCheckoutItems,
    preorderCheckoutItems,
  } = props

  const isShownDailyCard = useMemo(
    () =>
      dailySchedule &&
      !isTemporaryClose &&
      checkedOrderTypes.includes('daily') &&
      ((currentTab === 'released' &&
        // 自動配信で生成予定および都度配信で手動設定済
        ((dailySchedule.days_of_week.includes(dayOfWeek) && dailyStocks.length === 0) || // 自動配信で生成予定
          dailyStocks.some((stock) => stock.stock > 0))) || // 在庫設定済
        (currentTab === 'unreleased' &&
          // 在庫が削除（ゼロ）もしくは都度配信で未設定
          (dailyStocks.every((stock) => stock.stock === 0) ||
            (!dailySchedule.days_of_week.includes(dayOfWeek) && dailyStocks.length === 0)))),
    [dailySchedule, isTemporaryClose, checkedOrderTypes, currentTab, dailyStocks, dayOfWeek]
  )
  const isShownPreorderCard = useMemo(
    () =>
      preorderSchedule &&
      checkedOrderTypes.includes('preorder') &&
      ((currentTab === 'released' && preorderStocks.length > 0) ||
        (currentTab === 'unreleased' && preorderStocks.length === 0)),
    [preorderSchedule, checkedOrderTypes, currentTab, preorderStocks]
  )

  // 事前予約の注文締切前かどうか
  const isBeforePreorderDeadline = useMemo(() => {
    const now = new Date()
    if (preorderStocks.length > 0) {
      return preorderStocks.some(({ deadline }) => isBefore(now, deadline.toDate()))
    } else if (preorderSchedule) {
      return isBefore(now, addMinutes(date, -(preorderSchedule.hours_to_deadline * 60)))
    } else {
      return false
    }
  }, [preorderStocks, preorderSchedule, date])

  // タイムセール対象の商品情報を取得
  const storeTimesale: StoreTimesale = useSelector((state: State) => getStoreTimesale(state))

  // 販売予定の商品がタイムセール対象商品か検証
  const isTimeSaleItem =
    storeTimesale?.is_active &&
    storeTimesale?.breads.some((targetBread) => targetBread.bread_id === bread.bread_ref?.id)

  return (
    <CardInner>
      <FlexRow justifyContent={'start'}>
        <div className={`${styles.row__image}`}>
          <img src={bread.image_urls[0] || ''} alt={getBreadShortName(bread)} />
        </div>
        <Spacer size={12} />
        <div>
          <h4 className={typography.headline_small}>
            {getBreadShortName(bread)}
            {isTimeSaleItem && <TimeSaleTag tagName={'セール対象'} />}
          </h4>
          <Spacer size={8} />
          <FlexRow justifyContent={'start'}>
            <SmallLabel label={'カテゴリー ： '} />
            <Spacer size={2} />
            <GreyTag shape={'ellipse'} tagName={categoryName} />
          </FlexRow>
        </div>
      </FlexRow>
      <Spacer size={16} />
      {isShownDailyCard && (
        <ScheduledItemStockCard
          bread={bread}
          checkoutItems={dailyCheckoutItems}
          checkedOrderTypes={checkedOrderTypes}
          currentTab={currentTab}
          date={date}
          isAfterLastOrder={isAfterLastOrder}
          isFuture={isFuture}
          schedule={dailySchedule}
          stocks={dailyStocks}
          type={'daily'}
        />
      )}
      {isShownDailyCard && isShownPreorderCard && <Spacer size={8} />}
      {isShownPreorderCard && (
        <ScheduledItemStockCard
          bread={bread}
          checkoutItems={preorderCheckoutItems}
          checkedOrderTypes={checkedOrderTypes}
          currentTab={currentTab}
          date={date}
          isAfterLastOrder={isAfterLastOrder}
          isBeforePreorderDeadline={isBeforePreorderDeadline}
          isFuture={isFuture}
          schedule={preorderSchedule}
          stocks={preorderStocks}
          type={'preorder'}
        />
      )}
    </CardInner>
  )
}

export default ScheduledItemCard
